import { createAsyncThunk } from '@reduxjs/toolkit';

import { client } from 'src/graphql/client';
import { MOVE_DELIVERY_STOPS_TO_CUSTOMER_PICKUP } from 'src/graphql/requests/moveDeliveryStopsToCustomerPickup';
import { GMoveDeliveryStopsToCustomerPickupMutation } from 'src/graphql/types/generated';
import { logWarning } from 'src/services/datadog';
import { firstUserErrorMessage } from 'src/utils';
import { NotificationType, showNotification } from '../notification';
import { getAllStopStatuses } from './getAllStopStatues';
import { getPrimaryStopStatuses } from './getPrimaryStopStatuses';
import { getRoutes } from './getRoutes';

type MoveDeliveryStopsToCustomerPickupArgs = {
  stopIds: string[];
  warehouseId: string;
  nightEnd: string;
};

export const moveDeliveryStopsToCustomerPickup = createAsyncThunk(
  'delivery/moveDeliveryStopsToCustomerPickup',
  async (
    { stopIds, warehouseId, nightEnd }: MoveDeliveryStopsToCustomerPickupArgs,
    thunkAPI,
  ): Promise<boolean> => {
    if (stopIds.length === 0) return false;

    const { data, error } = await client
      .mutation<GMoveDeliveryStopsToCustomerPickupMutation>(
        MOVE_DELIVERY_STOPS_TO_CUSTOMER_PICKUP,
        { input: { stopIds } },
      )
      .toPromise();

    const payload = data?.moveDeliveryStopsToCustomerPickup;
    const userErrors = payload?.userErrors ?? [];

    if (error || !payload || userErrors.length > 0) {
      logWarning('customer_pickup.move_to_bucket_failed', {
        stopCount: stopIds.length,
        userError: userErrors[0]?.__typename,
        transportError: Boolean(error),
      });
      thunkAPI.dispatch(
        showNotification({
          type: NotificationType.Error,
          // PickupRoutingDisabled is the common one: this warehouse has the
          // Flipper off. The frontend has no read of that flag, so the server's
          // message is the only explanation available.
          message: userErrors.length
            ? firstUserErrorMessage(userErrors)
            : undefined,
        }),
      );
      if (userErrors.length > 0) {
        thunkAPI.dispatch(getRoutes({ warehouseId, nightEnd }));
      }
      return false;
    }

    thunkAPI.dispatch(
      showNotification({
        type: NotificationType.Success,
        message:
          stopIds.length === 1
            ? 'Stop moved to Customer Pick Ups.'
            : `${stopIds.length} stops moved to Customer Pick Ups.`,
      }),
    );

    // Refetching rather than merging the payload's routes: this call creates the
    // night's bucket when it does not exist yet, and a brand new route is not in
    // state.routes to be replaced by id, so a merge would drop it and leave the
    // tab empty. refreshRouteData also re-runs the pinning and refreshes both
    // stop-status caches behind the row icons.
    thunkAPI.dispatch(getRoutes({ warehouseId, nightEnd }));
    thunkAPI.dispatch(getAllStopStatuses({ warehouseId, nightEnd }));
    thunkAPI.dispatch(getPrimaryStopStatuses({ warehouseId, nightEnd }));

    return true;
  },
);
